import { TenantCredentialId } from "@sp-crm/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ContentFull, ContentHeader } from "components/layout";
import { trpc } from "components/rpc/trpc";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { InlineBanner } from "components/ui/inline-banner";
import { Input } from "components/ui/input";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import React from "react";
import { useFeature } from "store/selectors/hooks";

export const ManageIntegrationsWellSky: React.FC = () => {
    const isFeatureEnabled = useFeature("wellSkyIntegration");
    const trpcClient = trpc.useTRPC();

    const credentialQuery = useQuery(
        trpcClient.wellSkyPersonalCare.getCredential.queryOptions(undefined, {
            enabled: isFeatureEnabled,
        }),
    );

    const [showForm, setShowForm] = React.useState(false);
    const [usernameInput, setUsernameInput] = React.useState("");
    const [accountNameInput, setAccountNameInput] = React.useState("");
    const [passwordInput, setPasswordInput] = React.useState("");
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const [successMessage, setSuccessMessage] = React.useState<string | null>(null);

    const storeMutation = useMutation({
        ...trpcClient.wellSkyPersonalCare.storeCredential.mutationOptions(),
        onSuccess: () => {
            setErrorMessage(null);
            setSuccessMessage("WellSky credentials saved");
            setShowForm(false);
            setUsernameInput("");
            setAccountNameInput("");
            setPasswordInput("");
            credentialQuery.refetch();
        },
        onError: error => {
            setSuccessMessage(null);
            setErrorMessage(error.message || "Failed to save credentials");
        },
    });

    const deleteMutation = useMutation({
        ...trpcClient.wellSkyPersonalCare.deleteCredential.mutationOptions(),
        onSuccess: () => {
            setSuccessMessage("WellSky credentials removed");
            setErrorMessage(null);
            credentialQuery.refetch();
        },
        onError: error => {
            setErrorMessage(error.message || "Failed to remove credentials");
        },
    });

    const credential = credentialQuery.data;

    const handleStartEdit = React.useCallback(() => {
        setShowForm(true);
        setUsernameInput(credential?.username ?? "");
        setAccountNameInput(credential?.accountName ?? "");
        setPasswordInput("");
        setErrorMessage(null);
        setSuccessMessage(null);
    }, [credential?.username, credential?.accountName]);

    const handleCancel = React.useCallback(() => {
        setShowForm(false);
        setUsernameInput("");
        setAccountNameInput("");
        setPasswordInput("");
        setErrorMessage(null);
    }, []);

    const handleSave = React.useCallback(() => {
        setErrorMessage(null);
        setSuccessMessage(null);
        storeMutation.mutate({
            username: usernameInput,
            accountName: accountNameInput,
            password: passwordInput,
        });
    }, [usernameInput, accountNameInput, passwordInput, storeMutation]);

    const handleDelete = React.useCallback(
        async (id: TenantCredentialId) => {
            const confirmed = await fancyConfirm(
                "Remove WellSky credentials?",
                "The Send to WellSky Personal Care action will be unavailable until new credentials are added.",
                "Yes, remove",
                "Cancel",
            );
            if (confirmed) {
                deleteMutation.mutate({ id });
            }
        },
        [deleteMutation],
    );

    if (!isFeatureEnabled) {
        return (
            <ContentFull>
                <ContentHeader>WellSky Personal Care</ContentHeader>
                <InlineBanner type="info">
                    The WellSky Personal Care integration is not enabled for this account.
                </InlineBanner>
            </ContentFull>
        );
    }

    return (
        <ContentFull>
            <ContentHeader>WellSky Personal Care</ContentHeader>
            <div className="space-y-4">
                <div>
                    Connect your WellSky Personal Care account to send home care clients
                    to WellSky as new prospects.
                </div>

                {successMessage && (
                    <InlineBanner type="success">{successMessage}</InlineBanner>
                )}
                {errorMessage && <InlineBanner type="error">{errorMessage}</InlineBanner>}

                {credentialQuery.isLoading ? (
                    <div className="flex items-center space-x-2">
                        <Spinner />
                        <span>Loading...</span>
                    </div>
                ) : credential && !showForm ? (
                    <div className="p-3 border border-gray-100 rounded space-y-2">
                        <div>
                            <div className="text-sm text-gray-500">Account</div>
                            <div className="font-medium">{credential.accountName}</div>
                        </div>
                        <div>
                            <div className="text-sm text-gray-500">Username</div>
                            <div className="font-medium">{credential.username}</div>
                        </div>
                        <div className="flex space-x-2 pt-2">
                            <SecondaryButton onClick={handleStartEdit}>
                                Update credentials
                            </SecondaryButton>
                            <SecondaryButton onClick={() => handleDelete(credential.id)}>
                                Remove
                            </SecondaryButton>
                        </div>
                    </div>
                ) : null}

                {!credential && !showForm && (
                    <PrimaryButton onClick={() => setShowForm(true)}>
                        Connect WellSky
                    </PrimaryButton>
                )}

                {showForm && (
                    <div className="p-4 border border-gray-200 rounded space-y-4">
                        <h3 className="font-semibold text-lg">WellSky credentials</h3>
                        <Input
                            label="Account (full domain, e.g. abcalamance.clearcareonline.com)"
                            value={accountNameInput}
                            onChange={e => setAccountNameInput(e.target.value)}
                            placeholder="yourtenant.clearcareonline.com"
                        />
                        <Input
                            label="Username"
                            value={usernameInput}
                            onChange={e => setUsernameInput(e.target.value)}
                            placeholder="username@yourcompany.com"
                        />
                        <Input
                            label="Password"
                            type="password"
                            value={passwordInput}
                            onChange={e => setPasswordInput(e.target.value)}
                            placeholder="Enter your password"
                        />
                        <div className="flex justify-end space-x-2">
                            <SecondaryButton onClick={handleCancel}>
                                Cancel
                            </SecondaryButton>
                            <PrimaryButton
                                onClick={handleSave}
                                disabled={
                                    !usernameInput ||
                                    !accountNameInput ||
                                    !passwordInput ||
                                    storeMutation.isPending
                                }>
                                {storeMutation.isPending && <Spinner />}
                                <span className={storeMutation.isPending ? "ml-2" : ""}>
                                    Save
                                </span>
                            </PrimaryButton>
                        </div>
                    </div>
                )}
            </div>
        </ContentFull>
    );
};
